import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import {
  MAX_PORTFOLIO_IMAGES,
  providersService,
} from '../../shared/services/providers.service';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';

const ACCEPTED_TYPES = 'image/jpeg,image/png,image/webp,image/gif';
const ACCEPTED_MIMES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
]);
const MAX_BYTES = 10 * 1024 * 1024;

type UploadStatus = 'uploading' | 'done' | 'error';

interface Tile {
  id: string;
  file: File;
  previewUrl: string;
  status: UploadStatus;
  progress: number;
  uploadedUrl?: string;
  caption: string;
  error?: string;
}

export function AddPortfolioImagesBulkDialog({
  open,
  onClose,
  currentCount,
  targetProviderId,
  invalidateKeys,
}: {
  open: boolean;
  onClose: () => void;
  currentCount: number;
  /**
   * If set, runs in admin mode : the bulk attach call targets
   * `/api/v1/admin/providers/{targetProviderId}/portfolio/bulk` instead of
   * `/api/v1/providers/me/portfolio/bulk`. Caller must be ADMIN+.
   */
  targetProviderId?: string;
  /**
   * React-query cache keys to invalidate after a successful submit.
   * Defaults to the current user's provider profile (self-service mode).
   */
  invalidateKeys?: (string | readonly unknown[])[];
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [tiles, setTiles] = useState<Tile[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const remaining = MAX_PORTFOLIO_IMAGES - currentCount;
  const doneTiles = useMemo(() => tiles.filter((tx) => tx.status === 'done'), [tiles]);
  const uploadingCount = useMemo(
    () => tiles.filter((tx) => tx.status === 'uploading').length,
    [tiles],
  );
  const errorCount = useMemo(
    () => tiles.filter((tx) => tx.status === 'error').length,
    [tiles],
  );

  const updateTile = useCallback((id: string, patch: Partial<Tile>) => {
    setTiles((prev) => prev.map((tx) => (tx.id === id ? { ...tx, ...patch } : tx)));
  }, []);

  const startUploadFor = useCallback(
    async (tile: Tile) => {
      try {
        const result = await providersService.uploadPortfolioImage(
          tile.file,
          (p) => updateTile(tile.id, { progress: p }),
        );
        updateTile(tile.id, {
          status: 'done',
          progress: 100,
          uploadedUrl: result.url,
        });
      } catch (err) {
        updateTile(tile.id, {
          status: 'error',
          error: translate(err),
        });
      }
    },
    [updateTile, translate],
  );

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ''; // allow re-selecting the same files
    if (files.length === 0) return;

    const slotsLeft = remaining - tiles.length;
    if (slotsLeft <= 0) {
      notify.error(
        t('provider.bulkNoSlotsLeft', {
          max: MAX_PORTFOLIO_IMAGES,
          defaultValue: `Maximum ${MAX_PORTFOLIO_IMAGES} photos atteint.`,
        }),
      );
      return;
    }

    const accepted: Tile[] = [];
    let skippedTooLarge = 0;
    let skippedWrongType = 0;
    let skippedNoSlot = 0;

    for (const f of files) {
      if (accepted.length >= slotsLeft) {
        skippedNoSlot++;
        continue;
      }
      if (!ACCEPTED_MIMES.has(f.type)) {
        skippedWrongType++;
        continue;
      }
      if (f.size > MAX_BYTES) {
        skippedTooLarge++;
        continue;
      }
      accepted.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file: f,
        previewUrl: URL.createObjectURL(f),
        status: 'uploading',
        progress: 0,
        caption: '',
      });
    }

    if (skippedTooLarge) notify.warning(t('provider.fileTooLarge'));
    if (skippedWrongType) {
      notify.warning(
        t('provider.bulkInvalidType', {
          defaultValue: 'Type non supporté (JPG, PNG, WEBP, GIF uniquement).',
        }),
      );
    }
    if (skippedNoSlot) {
      notify.info(
        t('provider.bulkSomeSkipped', {
          count: skippedNoSlot,
          defaultValue: `${skippedNoSlot} fichier(s) ignoré(s), limite atteinte.`,
        }),
      );
    }

    if (accepted.length === 0) return;
    setTiles((prev) => [...prev, ...accepted]);
    // Fire uploads in parallel.
    accepted.forEach(startUploadFor);
  };

  const handleRemove = useCallback((id: string) => {
    setTiles((prev) => {
      const target = prev.find((tx) => tx.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((tx) => tx.id !== id);
    });
  }, []);

  const handleRetry = useCallback(
    (id: string) => {
      const tile = tiles.find((tx) => tx.id === id);
      if (!tile) return;
      updateTile(id, { status: 'uploading', progress: 0, error: undefined });
      startUploadFor({ ...tile, status: 'uploading', progress: 0, error: undefined });
    },
    [tiles, updateTile, startUploadFor],
  );

  const submit = useMutation({
    mutationFn: () => {
      const items = doneTiles.map((tx) => ({
        imageUrl: tx.uploadedUrl as string,
        caption: tx.caption.trim() || undefined,
      }));
      return targetProviderId
        ? providersService.adminAddPortfolioImagesBulk(targetProviderId, items)
        : providersService.addPortfolioImagesBulk(items);
    },
    onMutate: () => setSubmitting(true),
    onSettled: () => setSubmitting(false),
    onSuccess: (created) => {
      const keys = invalidateKeys ?? [['provider', 'me']];
      keys.forEach((k) =>
        qc.invalidateQueries({ queryKey: Array.isArray(k) ? k : [k] }),
      );
      notify.success(
        t('provider.bulkAddedCount', {
          count: created.length,
          defaultValue: `${created.length} photo(s) ajoutée(s).`,
        }),
      );
      cleanupAndClose();
    },
    onError: (err) => notify.error(translate(err)),
  });

  const cleanupAndClose = useCallback(() => {
    tiles.forEach((tx) => URL.revokeObjectURL(tx.previewUrl));
    setTiles([]);
    onClose();
  }, [tiles, onClose]);

  const handleClose = () => {
    if (submitting || uploadingCount > 0) return;
    cleanupAndClose();
  };

  const canSubmit =
    !submitting &&
    uploadingCount === 0 &&
    doneTiles.length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ pb: 1, pr: 6 }}>
        <Typography variant="h5" component="span">
          {t('provider.bulkAddTitle', {
            defaultValue: 'Ajouter plusieurs photos',
          })}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
          <Chip
            size="small"
            label={t('provider.bulkSelectedCount', {
              count: tiles.length,
              defaultValue: `${tiles.length} sélectionnée(s)`,
            })}
            color={tiles.length > 0 ? 'primary' : 'default'}
          />
          <Chip
            size="small"
            variant="outlined"
            label={t('provider.bulkSlotsLeft', {
              count: remaining - tiles.length,
              max: MAX_PORTFOLIO_IMAGES,
              defaultValue: `${remaining - tiles.length} sur ${MAX_PORTFOLIO_IMAGES} restantes`,
            })}
          />
          {errorCount > 0 && (
            <Chip
              size="small"
              color="error"
              label={t('provider.bulkErrorCount', {
                count: errorCount,
                defaultValue: `${errorCount} échec(s)`,
              })}
            />
          )}
        </Stack>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={submitting || uploadingCount > 0}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {remaining <= 0 ? (
            <Alert severity="info">
              {t('provider.portfolioMaxReached', {
                max: MAX_PORTFOLIO_IMAGES,
                defaultValue: `Maximum ${MAX_PORTFOLIO_IMAGES} photos atteint.`,
              })}
            </Alert>
          ) : (
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFileIcon />}
              disabled={tiles.length >= remaining || submitting}
              fullWidth
              size="large"
            >
              {tiles.length === 0
                ? t('provider.bulkPickFiles', {
                    defaultValue: 'Choisir des photos',
                  })
                : t('provider.bulkPickMore', {
                    defaultValue: 'Ajouter d\'autres photos',
                  })}
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_TYPES}
                multiple
                hidden
                onChange={handleFilesSelected}
              />
            </Button>
          )}

          {uploadingCount > 0 && (
            <LinearProgress
              variant="indeterminate"
              sx={{ borderRadius: 1, height: 6 }}
            />
          )}

          {tiles.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(3, 1fr)',
                  md: 'repeat(4, 1fr)',
                },
                gap: 1.5,
              }}
            >
              {tiles.map((tx) => (
                <TileCard
                  key={tx.id}
                  tile={tx}
                  disabled={submitting}
                  onRemove={() => handleRemove(tx.id)}
                  onRetry={() => handleRetry(tx.id)}
                  onCaptionChange={(c) => updateTile(tx.id, { caption: c })}
                />
              ))}
            </Box>
          )}

          {tiles.length === 0 && remaining > 0 && (
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
              {t('provider.bulkEmptyHint', {
                defaultValue:
                  'Sélectionnez plusieurs photos (JPG, PNG, WEBP, GIF · max 10 Mo chacune).',
              })}
            </Typography>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          disabled={submitting || uploadingCount > 0}
        >
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!canSubmit}
          onClick={() => submit.mutate()}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitting
            ? t('common.loading')
            : t('provider.bulkSubmit', {
                count: doneTiles.length,
                defaultValue: `Envoyer ${doneTiles.length} photo(s)`,
              })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TileCard({
  tile,
  disabled,
  onRemove,
  onRetry,
  onCaptionChange,
}: {
  tile: Tile;
  disabled: boolean;
  onRemove: () => void;
  onRetry: () => void;
  onCaptionChange: (c: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <Stack
      spacing={0.75}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor:
          tile.status === 'error' ? 'error.main' : 'divider',
        p: 1,
        bgcolor: (theme) =>
          theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.02)'
            : 'rgba(0,0,0,0.02)',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '1 / 1',
          borderRadius: 1.5,
          overflow: 'hidden',
          bgcolor: 'background.default',
        }}
      >
        <Box
          component="img"
          src={tile.previewUrl}
          alt={tile.file.name}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            filter: tile.status === 'uploading' ? 'brightness(0.7)' : 'none',
          }}
        />

        {/* Status overlay */}
        {tile.status === 'uploading' && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'common.white',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <CircularProgress
              size={32}
              variant="determinate"
              value={tile.progress}
              sx={{ color: 'common.white' }}
            />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {tile.progress}%
            </Typography>
          </Box>
        )}
        {tile.status === 'done' && (
          <CheckCircleRoundedIcon
            sx={{
              position: 'absolute',
              top: 6,
              left: 6,
              color: 'success.main',
              bgcolor: 'common.white',
              borderRadius: '50%',
              fontSize: 22,
            }}
          />
        )}
        {tile.status === 'error' && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0,0,0,0.55)',
              color: 'common.white',
              flexDirection: 'column',
              gap: 0.5,
              p: 1,
              textAlign: 'center',
            }}
          >
            <ErrorOutlineRoundedIcon fontSize="medium" />
            <Button
              size="small"
              variant="contained"
              color="warning"
              onClick={onRetry}
              disabled={disabled}
              sx={{ minHeight: 28, fontSize: '0.7rem' }}
            >
              {t('common.retry', { defaultValue: 'Réessayer' })}
            </Button>
          </Box>
        )}

        {/* Remove button (always shown unless submitting) */}
        <IconButton
          size="small"
          onClick={onRemove}
          disabled={disabled}
          aria-label="remove"
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            bgcolor: 'rgba(0,0,0,0.55)',
            color: 'common.white',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
            width: 28,
            height: 28,
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <TextField
        size="small"
        variant="standard"
        placeholder={t('provider.captionPlaceholder')}
        value={tile.caption}
        onChange={(e) => onCaptionChange(e.target.value.slice(0, 280))}
        disabled={tile.status !== 'done' || disabled}
        inputProps={{ maxLength: 280, 'aria-label': 'caption' }}
        sx={{
          '& .MuiInput-input': { fontSize: '0.8rem', py: 0.5 },
        }}
      />
    </Stack>
  );
}
